'use client';

import {
  HTMLAttributeAnchorTarget,
  type ReactElement,
  type ReactNode,
} from 'react';

import Link from 'next/link';

import { Button as AntButton, type ButtonProps as AntButtonProps } from 'antd';
import classNames from 'classnames';

export interface ButtonProps extends Omit<AntButtonProps, 'type'> {
  type?:
    | 'primary'
    | 'white'
    | 'tonal'
    | 'outline'
    | 'link'
    | 'black'
    | 'black-gradient'
    | 'big-white'
    | 'cart'
    | 'gray'
    | 'danger-outline'
    | 'filterTag';
  children?: ReactNode;
  fixed?: boolean;
  link?: string;
}

export interface LinkComponentProps {
  link?: string;
  children: ReactNode;
  target: HTMLAttributeAnchorTarget | undefined;
  rel: string | undefined;
  className?: string;
  id?: string;
}

const LinkComponent = ({
  link,
  target,
  rel,
  children,
  className,
  id,
}: LinkComponentProps) => {
  if (link) {
    return (
      <Link
        id={id}
        className={classNames(className)}
        target={target || undefined}
        rel={rel || undefined}
        href={link}
      >
        {children}
      </Link>
    );
  }
  return <>{children}</>;
};

export default function Button({
  children,
  type,
  className,
  fixed,
  link,
  target,
  rel,
  id,
  ...rest
}: ButtonProps): ReactElement {
  return (
    <LinkComponent
      id={id}
      target={target}
      rel={rel}
      link={link}
      className={className}
    >
      <AntButton
        id={id || undefined}
        style={{ height: 'auto' }}
        rootClassName="button"
        className={classNames(
          'z-50 h-11 rounded-md text-xs font-medium',
          {
            'blackBtn !bg-black-default !text-white !py-2.5': type === 'black',
          },
          {
            'whiteBtn !border-white !bg-white !text-black-default !py-2.5 hover:!border-white':
              type === 'white',
          },
          {
            'black-gradient-btn !py-3 !px-8.75': type === 'black-gradient',
          },
          {
            '!py-3 !px-8.75 border-black! hover:!text-black':
              type === 'big-white',
          },
          {
            'cart-btn !bg-silver-200 !text-secondary': type === 'cart',
          },
          { 'tonalBtn !bg-silver-300 !text-black-default': type === 'tonal' },
          {
            'border-1 outlineBtn !border-blue-default !bg-transparent !text-blue-default':
              type === 'outline',
          },
          {
            'border-1 outlineDangerBtn !border-red !bg-transparent !text-blue-default':
              type === 'danger-outline',
          },
          {
            'linkBtn h-auto rounded-sm !bg-transparent p-0 !text-blue-default':
              type === 'link',
          },
          {
            'rounded-lg !bg-silver-200 px-4 py-2 font-semibold !text-secondary-400':
              type === 'gray',
          },
          {
            'rounded-[10px] border border-[#EDECEB] bg-[#F2F1F1] px-4 py-2 text-sm font-medium !text-black-default hover:!border-solid hover:!text-black-default lg:rounded-[10px]':
              type === 'filterTag',
          },
          {
            'primaryBtn !bg-blue-default !text-white':
              type === 'primary' || !type,
          },
          [fixed && 'fixed bottom-6 right-4 !w-[calc(100%_-_32px)] '],
          className
        )}
        {...rest}
      >
        {children}
      </AntButton>
    </LinkComponent>
  );
}
